import { useCallback, useEffect, useState } from "react";

import type { ActionMeta } from "@des/react-select";

import type { StoreLocatorFilter, StoreLocatorFilterItem, StoreLocatorSelectedFilter } from "../../types";
import { OPEN_NOW_CATEGORY, OPEN_NOW_FILTER_OBJ } from "../../utils";

import type { UseStoreFiltersProps, UseStoreFiltersReturnType } from "./useStoreFilters.types";

type ApplyFiltersParams = {
  categoryToSkip?: string;
  additionalCategory?: Record<string, StoreLocatorFilterItem[]>;
};

type ConvertSelectedToActiveFIltersParams = {
  selectedFilters: Record<string, StoreLocatorSelectedFilter>;
  allFilters: StoreLocatorFilter;
  getFromInitialSelectedFilters?: boolean;
};

const isStoreLocatorFilterItem = (value: unknown): value is StoreLocatorFilterItem => {
  return typeof value === "object" && value !== null && "key" in value && typeof value.key === "string";
};

const convertSelectedToActiveFilters = ({
  selectedFilters,
  allFilters,
  getFromInitialSelectedFilters,
}: ConvertSelectedToActiveFIltersParams) => {
  const activeFilters: Record<string, StoreLocatorFilterItem[]> = {};

  for (const selectedFilter in selectedFilters) {
    if (selectedFilter === OPEN_NOW_CATEGORY) {
      activeFilters[OPEN_NOW_CATEGORY] = [OPEN_NOW_FILTER_OBJ];
      continue;
    }

    const [categoryKey, filterKey] = selectedFilter.split(".");
    if (!categoryKey || !filterKey) continue;

    if (categoryKey === OPEN_NOW_CATEGORY) {
      activeFilters[categoryKey] = [OPEN_NOW_FILTER_OBJ];
      continue;
    }

    if (selectedFilters[selectedFilter]?.selected) {
      if (getFromInitialSelectedFilters) {
        const filterObj = {
          name: "",
          key: filterKey,
          value: filterKey,
        };

        if (activeFilters[categoryKey]) {
          activeFilters[categoryKey].push(filterObj);
        } else {
          activeFilters[categoryKey] = [filterObj];
        }
        continue;
      }

      const categoryFilters = allFilters.find((filterCategory) => filterCategory.key === categoryKey);
      const filter = categoryFilters?.filters.find((filter) => filter?.key === filterKey);
      if (!filter) continue;

      if (activeFilters[categoryKey]) {
        activeFilters[categoryKey].push(filter);
      } else {
        activeFilters[categoryKey] = [filter];
      }
    }
  }

  return activeFilters;
};

const toggleFilterInCategory = (
  categoryFilters: StoreLocatorFilterItem[],
  targetFilter: StoreLocatorFilterItem
): StoreLocatorFilterItem[] => {
  const isFilterSelected = categoryFilters.some((existingFilter) => existingFilter?.key === targetFilter.key);

  return isFilterSelected
    ? categoryFilters.filter((existingFilter) => existingFilter?.key !== targetFilter.key)
    : [...categoryFilters, targetFilter];
};

export const useStoreFilters = ({
  selectedFilters,
  allFilters,
  handleChangeFilters,
  isOpenNowFilterEnabled,
}: UseStoreFiltersProps): UseStoreFiltersReturnType => {
  const [activeFilters, setActiveFilters] = useState<Record<string, StoreLocatorFilterItem[]>>(
    convertSelectedToActiveFilters({ selectedFilters, allFilters, getFromInitialSelectedFilters: true })
  );

  const applyFilters = (params: ApplyFiltersParams | unknown[] = {}) => {
    const { categoryToSkip, additionalCategory = {} } = Array.isArray(params) ? {} : params;
    const filtersCategories = { ...activeFilters, ...additionalCategory };
    const currentFilters: Record<string, StoreLocatorSelectedFilter> = {};

    const categoriesToProcess = Object.keys(filtersCategories).filter((category) => category !== categoryToSkip);

    for (const category of categoriesToProcess) {
      const validFilters =
        filtersCategories[category]?.filter((filter): filter is StoreLocatorFilterItem => Boolean(filter)) || [];

      for (const filter of validFilters) {
        const filterKey = `${category}.${filter.key}`;
        currentFilters[filterKey] = {
          key: filter.key,
          selected: true,
          category,
        };
      }
    }

    handleChangeFilters(currentFilters);
  };

  const changeFilters = useCallback((category: string, filter: StoreLocatorFilterItem) => {
    setActiveFilters((prevFilters) => {
      const currentCategoryFilters = prevFilters[category] ?? [];
      const updatedCategoryFilters = toggleFilterInCategory(currentCategoryFilters, filter);

      return {
        ...prevFilters,
        [category]: updatedCategoryFilters,
      };
    });
  }, []);

  const checkFiltersStatus = useCallback(
    (category: string, filterKey: string) => {
      return activeFilters[category]?.some((f) => f?.key === filterKey) || false;
    },
    [activeFilters]
  );

  useEffect(() => {
    if (selectedFilters) {
      setActiveFilters(convertSelectedToActiveFilters({ selectedFilters, allFilters }));
    }
  }, [allFilters, selectedFilters]);

  const handleApplyOpenNowCheckbox = () => {
    if (!isOpenNowFilterEnabled) return;

    const applyFiltersParams: ApplyFiltersParams = {};
    if (activeFilters[OPEN_NOW_CATEGORY]) {
      applyFiltersParams.categoryToSkip = OPEN_NOW_CATEGORY;
    } else {
      applyFiltersParams.additionalCategory = {
        [OPEN_NOW_CATEGORY]: [OPEN_NOW_FILTER_OBJ],
      };
    }
    applyFilters(applyFiltersParams);
  };

  const handleDropdownSelect = (categoryKey: string, _selectedOption: unknown, e: ActionMeta<unknown>) => {
    if ("option" in e && isStoreLocatorFilterItem(e.option)) changeFilters(categoryKey, e.option);
  };

  const handleDropdownRestart = (categoryKey: string) => {
    applyFilters({ categoryToSkip: categoryKey });
  };

  return {
    handleDropdownRestart,
    handleDropdownSelect,
    handleApplyOpenNowCheckbox,
    checkFiltersStatus,
    changeFilters,
    applyFilters,
    activeFilters,
  };
};
